import { useCallback, type RefObject } from "react";
import { IntelligenceEngineApi } from "@src/services/extractionApi";
import { getContentTypeFromExtension } from "@utils/index";
import {
  ProcessStatus,
  type IProcessInfoViewModel,
  ApiError,
} from "@models/index";
import type { ExtractionOptions } from "./useExtraction";

export const useFileUpload = (
  intelligenceEngineApi: IntelligenceEngineApi,
  setIsExtracting: (value: boolean) => void,
  setError: (error: string | null) => void,
  setProcessResultsInfo: React.Dispatch<
    React.SetStateAction<IProcessInfoViewModel[]>
  >,
  connectAndWaitForAuth: (processId: string) => Promise<void>,
  showError: (message: string) => void,
  showSuccess: (message: string) => void,
  setUploadProgress: (value: number) => void,
  doctypeCode: string,
  uploadingProcessIdsRef: RefObject<Set<string>>,
) => {
  const startExtraction = useCallback(
    async (file: File, options?: ExtractionOptions) => {
      setIsExtracting(true);
      setError(null);
      setUploadProgress(0);
      const contentType = getContentTypeFromExtension(file.name);
      let inFlightProcessId: string | null = null;

      try {
        if (!contentType) {
          throw new Error(
            "Unsupported file type. Please upload an image or PDF file.",
          );
        }

        if (!doctypeCode) {
          throw new Error(
            "Document type is required. Please select a document type first.",
          );
        }

        const presignedResponse =
          await intelligenceEngineApi.requestPresignedUrl(
            file.name,
            doctypeCode,
            options?.pageNumbers,
            options?.instructions,
            options?.mappingEnabled,
          );

        // Mark this process as locally in-flight so any list refetch that
        // lands during the upload filters it out (backend already has the
        // PENDING record from the presigned-URL POST above).
        inFlightProcessId = presignedResponse.id;
        uploadingProcessIdsRef.current.add(inFlightProcessId);

        // Wait for WebSocket to connect and authenticate before uploading.
        // This ensures the backend can send status updates from the start.
        await connectAndWaitForAuth(presignedResponse.id);

        await intelligenceEngineApi.uploadFileToS3(
          presignedResponse.upload_url,
          file,
          contentType,
          setUploadProgress,
        );

        // Insert the row into the processed list only after the S3 upload
        // completes — keeps the list clean while the progress bar runs.
        const newProcessViewModel: IProcessInfoViewModel = {
          id: presignedResponse.id,
          status: ProcessStatus.Pending,
          createdAt: new Date().toISOString(),
          isBatch: false,
          errorMessage: null,
          items: [
            {
              id: presignedResponse.id,
              fileName: file.name,
              status: ProcessStatus.Pending,
              demoImage: null,
              createdAt: new Date().toISOString(),
              errorMessage: null,
            },
          ],
          mappingEnabled: options?.mappingEnabled ?? false,
        };

        setProcessResultsInfo((prevResults) => [
          newProcessViewModel,
          ...prevResults,
        ]);

        showSuccess(`"${file.name}" uploaded successfully`);
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Upload failed";
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        if (inFlightProcessId) {
          uploadingProcessIdsRef.current.delete(inFlightProcessId);
        }
        setIsExtracting(false);
        setUploadProgress(0);
      }
    },
    [
      intelligenceEngineApi,
      setIsExtracting,
      setError,
      setProcessResultsInfo,
      connectAndWaitForAuth,
      showError,
      showSuccess,
      setUploadProgress,
      doctypeCode,
      uploadingProcessIdsRef,
    ],
  );

  return { startExtraction };
};
